import React from 'react';
import { Breakpoint, useBreakpoint } from './BreakpointProvider';

type ColumnCounts = Partial<Record<Breakpoint, number>>;

interface GridProps {
  children: React.ReactNode;
  // Columns per breakpoint; a missing entry falls back to the next smaller breakpoint, then 1
  cols?: ColumnCounts;
  // Alternative to cols: as many columns as fit at this minimum width
  minColWidth?: number;
  gap?: number | string;
  rowGap?: number | string;
  // Desktop-only arrangement: below desktop the children render as they are, with no wrapper
  onlyOnDesktop?: boolean;
  style?: React.CSSProperties;
}

const columnsFor = (bp: Breakpoint, cols: ColumnCounts): number => {
  const mobile = cols.mobile || 1;
  const tablet = cols.tablet || mobile;
  if (bp === 'mobile') return mobile;
  if (bp === 'tablet') return tablet;
  return cols.desktop || tablet;
};

const Grid = ({ children, cols = {}, minColWidth, gap = 8, rowGap, onlyOnDesktop, style }: GridProps) => {
  const { bp } = useBreakpoint();
  if (onlyOnDesktop && bp !== 'desktop') return <>{children}</>;
  const template = minColWidth
    ? `repeat(auto-fill, minmax(min(${minColWidth}px, 100%), 1fr))`
    : `repeat(${columnsFor(bp, cols)}, minmax(0, 1fr))`;
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: template,
      columnGap: gap,
      rowGap: rowGap === undefined ? gap : rowGap,
      ...style,
    }}>
      {children}
    </div>
  );
};

export default Grid;
